import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/SecurityDashboard.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api';
let toastId = 0;

const SecurityDashboard = () => {
  const navigate = useNavigate();

  const [stats, setStats]             = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [toasts, setToasts]           = useState([]);
  const intervalRef                   = useRef(null);

  const [activeTab, setActiveTab]     = useState('nodes');
  const [auditBlocks, setAuditBlocks]         = useState([]);
  const [auditLoading, setAuditLoading]       = useState(false);
  const [auditPage, setAuditPage]             = useState(0);
  const [auditTotalPages, setAuditTotalPages] = useState(0);

  const [selectedSource, setSelectedSource]   = useState({});
  const [recoveringNodes, setRecoveringNodes] = useState({});

  const [nodeModal, setNodeModal]               = useState(null);
  const [nodeBlocks, setNodeBlocks]             = useState([]);
  const [nodeBlocksLoading, setNodeBlocksLoading] = useState(false);
  const [nodeBlocksError, setNodeBlocksError]   = useState('');

  // backup & restore
  const [backups, setBackups]               = useState([]);
  const [backupsLoading, setBackupsLoading] = useState(false);
  const [backingUp, setBackingUp]           = useState(false);
  const [restoringFile, setRestoringFile]   = useState(null);  // filename being restored
  const [deletingFile, setDeletingFile]     = useState(null);  // filename being deleted
  const [confirmRestore, setConfirmRestore] = useState(null);  // filename pending confirm

  // attack simulation
  const [attackLoading, setAttackLoading] = useState({});
  const [attackLogs, setAttackLogs]       = useState([]);
  const [attackTargets, setAttackTargets] = useState({
    tamperData: 'NODE_C',
    corruptChain: 'NODE_C',
    killNode: 'NODE_B',
    restoreNode: 'NODE_B',
  });
  const [tamperBlocks, setTamperBlocks]       = useState([]);   // danh sách block để chọn
  const [selectedTamperBlock, setSelectedTamperBlock] = useState(''); // blockIndex đã chọn

  // helpers
  const token   = () => (JSON.parse(localStorage.getItem('user') || '{}')).token || '';
  const headers = () => ({ Authorization: `Bearer ${token()}` });

  const showToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = ++toastId;
    setToasts(prev => [...prev, { id, message, type }]);
    if (duration > 0) setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
  }, []);

  const dismissToast = id => setToasts(prev => prev.filter(t => t.id !== id));

  // fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/blockchain/stats`, { headers: headers() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStats(await res.json());
      setLastRefresh(new Date());
      setError('');
    } catch (e) {
      setError('Không thể tải thống kê blockchain: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  // fetch audit
  const fetchAuditBlocks = async (page = 0) => {
    setAuditLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/blockchain/audit/list/decoded?page=${page}&size=10`, { headers: headers() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setAuditBlocks(data.content || []);
      setAuditTotalPages(data.totalPages || 0);
      setAuditPage(page);
    } catch (e) {
      console.error('audit fetch failed:', e);
    } finally {
      setAuditLoading(false);
    }
  };

  // node chain modal
  const openNodeModal = async (node) => {
    if (node.status === 'OFFLINE') {
      showToast(`Node ${node.nodeId} đang OFFLINE, không thể xem blocks`, 'error');
      return;
    }
    setNodeModal(node);
    setNodeBlocks([]);
    setNodeBlocksError('');
    setNodeBlocksLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/blockchain/nodes/${node.nodeId}/chain`, { headers: headers() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setNodeBlocks(Array.isArray(data) ? data : []);
    } catch (e) {
      setNodeBlocksError('Không thể tải blocks: ' + e.message);
    } finally {
      setNodeBlocksLoading(false);
    }
  };

  const closeNodeModal = () => { setNodeModal(null); setNodeBlocks([]); setNodeBlocksError(''); };

  // ── backup functions ─────────────────────────────────────────────────────
  const fetchBackups = async () => {
    setBackupsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/blockchain/admin/backups`, { headers: headers() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setBackups(await res.json());
    } catch (e) {
      showToast('Không thể tải danh sách backup: ' + e.message, 'error');
    } finally {
      setBackupsLoading(false);
    }
  };

  const createBackup = async () => {
    setBackingUp(true);
    const pendingId = ++toastId;
    setToasts(prev => [...prev, { id: pendingId, message: '⏳ Đang tạo backup...', type: 'info' }]);
    try {
      const res  = await fetch(`${API_BASE}/blockchain/admin/backup`, { method: 'POST', headers: headers() });
      const data = await res.json();
      setToasts(prev => prev.filter(t => t.id !== pendingId));
      if (res.ok) {
        showToast(`✅ Backup thành công: ${data.filename} (${formatSize(data.sizeBytes)})`, 'success', 6000);
        fetchBackups();
      } else {
        showToast(`❌ Backup thất bại: ${data.message}`, 'error', 6000);
      }
    } catch (e) {
      setToasts(prev => prev.filter(t => t.id !== pendingId));
      showToast('❌ Lỗi kết nối khi tạo backup', 'error');
    } finally {
      setBackingUp(false);
    }
  };

  const doRestore = async (filename) => {
    setConfirmRestore(null);
    setRestoringFile(filename);
    const pendingId = ++toastId;
    setToasts(prev => [...prev, { id: pendingId, message: `⏳ Đang restore từ ${filename}...`, type: 'info' }]);
    try {
      const res  = await fetch(
        `${API_BASE}/blockchain/admin/restore?filename=${encodeURIComponent(filename)}`,
        { method: 'POST', headers: headers() }
      );
      const data = await res.json();
      setToasts(prev => prev.filter(t => t.id !== pendingId));
      if (res.ok) {
        showToast(`✅ ${data.message}`, 'success', 8000);
        setTimeout(fetchStats, 2000);
      } else {
        showToast(`❌ Restore thất bại: ${data.message}`, 'error', 6000);
      }
    } catch (e) {
      setToasts(prev => prev.filter(t => t.id !== pendingId));
      showToast('❌ Lỗi kết nối khi restore', 'error');
    } finally {
      setRestoringFile(null);
    }
  };

  const deleteBackup = async (filename) => {
    setDeletingFile(filename);
    try {
      const res = await fetch(
        `${API_BASE}/blockchain/admin/backups?filename=${encodeURIComponent(filename)}`,
        { method: 'DELETE', headers: headers() }
      );
      if (res.ok) {
        showToast(`🗑️ Đã xóa ${filename}`, 'info');
        setBackups(prev => prev.filter(b => b.filename !== filename));
      } else {
        showToast('❌ Không thể xóa backup', 'error');
      }
    } catch (e) {
      showToast('❌ Lỗi kết nối khi xóa backup', 'error');
    } finally {
      setDeletingFile(null);
    }
  };

  const formatSize = bytes => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  // ── attack functions ─────────────────────────────────────────────────────
  const runAttack = async (type, targetNode) => {
    setAttackLoading(prev => ({ ...prev, [type]: true }));
    const ts = new Date().toLocaleTimeString('vi-VN');
    try {
      const url = `${API_BASE}/blockchain/admin/attack/${type}?targetNode=${encodeURIComponent(targetNode)}`
                + (type === 'tamper-data' && selectedTamperBlock ? `&blockIndex=${selectedTamperBlock}` : '');
      const res  = await fetch(url, { method: 'POST', headers: headers() });
      const data = await res.json();

      const logEntry = {
        id: Date.now(),
        ts,
        type,
        targetNode,
        success: data.success,
        description: data.description,
        details: data.details,
      };
      setAttackLogs(prev => [logEntry, ...prev].slice(0, 20)); // keep last 20

      if (data.success) {
        // Màu toast theo loại attack: đỏ cho tấn công, xanh cho restore
        const isAttack = ['tamper-data', 'corrupt-chain', 'kill-node'].includes(type);

        if (type === 'restore-node' && data.success) {
          showToast(`✅ ${data.description}`, 'success', 3000);
          showToast(`⏳ Node ${targetNode} đang tự động đồng bộ `, 'info', 8000);
          setTimeout(fetchStats, 2000);
          // Sau 10s hiện toast đồng bộ hoàn thành và refresh lại dashboard
          setTimeout(() => {
            showToast(`🔄 Node ${targetNode} đã đồng bộ xong!`, 'success', 4000);
            fetchStats();
          }, 15000);
        } else {
          showToast(`${isAttack ? '💥' : '✅'} ${data.description}`, isAttack ? 'error' : 'success', 5000);
          setTimeout(fetchStats, 2000);
        }
      } else {
        showToast(`⚠️ ${data.description}`, 'error');
      }
    } catch (e) {
      const logEntry = { id: Date.now(), ts, type, targetNode, success: false, description: 'Network error: ' + e.message };
      setAttackLogs(prev => [logEntry, ...prev].slice(0, 20));
      showToast('❌ Lỗi kết nối khi thực hiện attack', 'error');
    } finally {
      setAttackLoading(prev => ({ ...prev, [type]: false }));
    }
  };

  const ATTACK_NODES = stats ? stats.nodes.map(n => n.nodeId) : ['NODE_A', 'NODE_B', 'NODE_C'];

  const fetchTamperBlocks = async (nodeId) => {
    try {
      const res = await fetch(
        `${API_BASE}/blockchain/admin/attack/chain-blocks?targetNode=${nodeId}`,
        { headers: headers() }
      );
      if (res.ok) {
        const data = await res.json();
        setTamperBlocks(Array.isArray(data) ? data : []);
        setSelectedTamperBlock(''); // reset selection
      }
    } catch (e) {
      console.error('fetchTamperBlocks failed:', e);
    }
  };

  // recover
  const recoverNode = async (targetNodeId, sourceNodeId) => {
    if (!sourceNodeId) { showToast('Vui lòng chọn node nguồn để phục hồi', 'error'); return; }
    setRecoveringNodes(prev => ({ ...prev, [targetNodeId]: true }));
    const pendingId = ++toastId;
    setToasts(prev => [...prev, { id: pendingId, message: `⏳ Đang phục hồi ${targetNodeId} từ ${sourceNodeId}...`, type: 'info' }]);
    try {
      const res  = await fetch(`${API_BASE}/blockchain/nodes/${targetNodeId}/recover/${sourceNodeId}`, { method: 'POST', headers: headers() });
      const data = await res.json();
      setToasts(prev => prev.filter(t => t.id !== pendingId));
      if (res.ok) {
        showToast(`✅ ${data.message}`, 'success', 6000);
        setSelectedSource(prev => { const n = { ...prev }; delete n[targetNodeId]; return n; });
        setStats(prev => prev ? {
          ...prev,
          nodes: prev.nodes.map(n => n.nodeId === targetNodeId ? { ...n, status: 'ONLINE' } : n),
          integrityStatus: 'HEALTHY',
        } : prev);
        setTimeout(fetchStats, 3000);
      } else {
        showToast(`❌ Phục hồi thất bại: ${data.error}`, 'error', 6000);
      }
    } catch (err) {
      setToasts(prev => prev.filter(t => t.id !== pendingId));
      showToast(`❌ Lỗi kết nối khi phục hồi node ${targetNodeId}`, 'error', 6000);
    } finally {
      setRecoveringNodes(prev => ({ ...prev, [targetNodeId]: false }));
    }
  };

  // lifecycle
  useEffect(() => {
    fetchStats();
    intervalRef.current = setInterval(fetchStats, 10000);
    return () => clearInterval(intervalRef.current);
  }, [fetchStats]);

  useEffect(() => { if (activeTab === 'audit') fetchAuditBlocks(0); }, [activeTab]);
  useEffect(() => { if (activeTab === 'backup') fetchBackups(); }, [activeTab]);

  // style helpers
  const getNodeColor = status => ({
    ONLINE:   { bg: '#1a3a1a', color: '#3fb950', dot: '#3fb950' },
    OFFLINE:  { bg: '#2a1a1a', color: '#f85149', dot: '#f85149' },
    SYNCING:  { bg: '#3a2d0a', color: '#e3b341', dot: '#e3b341' },
    TAMPERED: { bg: '#3a1a1a', color: '#f85149', dot: '#f85149' },
  }[status] || { bg: '#1e1e1e', color: '#8b949e', dot: '#8b949e' });

  const getIntegrityColor = s => s === 'HEALTHY' ? '#3fb950' : s === 'CHAIN_TAMPERED' ? '#f85149' : '#8b949e';
  const formatTs   = ts => ts ? new Date(ts).toLocaleString('vi-VN') : 'Chưa kiểm tra';
  const formatDt   = dt => dt ? new Date(dt).toLocaleString('vi-VN') : '—';
  const shortHash  = h  => h  ? h.slice(0, 12) + '…' : '—';
  const typeBadge  = t  => ({ MATCH: '#1f6feb', TRANSACTION: '#2da44e', AUDIT: '#8957e5', GENESIS: '#6e7681' }[t] || '#6e7681');

  const statCards = stats ? [
    { label: 'Blocks',        value: stats.totalBlocks,         icon: '🔗' },
    { label: 'Matches',       value: stats.matchCount,          icon: '🎮' },
    { label: 'Transactions',  value: stats.transactionCount,    icon: '💰' },
    { label: 'Audits',        value: stats.auditCount,          icon: '📋' },
    { label: 'Encrypted',     value: stats.encryptedBackupCount, icon: '�' },
    { label: 'Nodes Online',  value: stats.totalNodesOnline,    icon: '🖥️' },
  ] : [];

  return (
    <div className="sec-dashboard">

      {/* Toasts */}
      <div className="sec-toast-container">
        {toasts.map(t => (
          <div key={t.id} className={`sec-toast ${t.type}`}>
            <span className="sec-toast-icon">{t.type === 'success' ? '' : t.type === 'error' ? '' : 'ℹ️'}</span>
            <span className="sec-toast-body">{t.message}</span>
            <button className="sec-toast-close" onClick={() => dismissToast(t.id)}>✕</button>
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="sec-header">
        <div>
          <h1>🛡️ Security Dashboard</h1>
          <p className="sec-subtitle">Private Blockchain Prototype — Integrity Monitoring</p>
        </div>
        <div className="sec-header-right">
          {lastRefresh && (
            <div className="sec-refresh-info">
              <span>Auto-refresh: 10s</span>
              <span>Cập nhật: {lastRefresh.toLocaleTimeString('vi-VN')}</span>
            </div>
          )}
          <div className="sec-header-actions">
            <button className="sec-action-btn" onClick={() => navigate('/home')}>🏠 Home</button>
            <a href="/blockchain/explorer" className="sec-action-btn">🔗 Explorer</a>
          </div>
        </div>
      </div>

      {error && <div className="sec-error">⚠️ {error}</div>}

      {loading ? (
        <div className="sec-loading">⏳ Đang tải dữ liệu bảo mật…</div>
      ) : stats && (
        <div className="sec-body">
          {/* Tabs */}
          <div className="sec-tabs">
            <button className={`sec-tab ${activeTab === 'nodes' ? 'active' : ''}`} onClick={() => setActiveTab('nodes')}>
              🖥️ Node Status &amp; Stats
            </button>
            <button className={`sec-tab ${activeTab === 'audit' ? 'active' : ''}`} onClick={() => setActiveTab('audit')}>
              📋 Audit Log
            </button>
            <button className={`sec-tab ${activeTab === 'backup' ? 'active' : ''}`} onClick={() => setActiveTab('backup')}>
              💾 Backup &amp; Restore
            </button>
            <button className={`sec-tab sec-tab-danger ${activeTab === 'attack' ? 'active' : ''}`} onClick={() => setActiveTab('attack')}>
              🔴 Attack Simulation
            </button>
          </div>

          {/* Tab: Nodes */}
          {activeTab === 'nodes' && (
            <div className="sec-tab-content">
              <div className="sec-stats-grid">
                {statCards.map(c => (
                  <div key={c.label} className="sec-stat-card">
                    <div className="sec-stat-icon">{c.icon}</div>
                    <div className="sec-stat-value">{(c.value ?? 0).toLocaleString()}</div>
                    <div className="sec-stat-label">{c.label}</div>
                  </div>
                ))}
              </div>

              <div className="sec-row">
                {/* Integrity card */}
                <div className="sec-card">
                  <h2>🔐 Blockchain Integrity</h2>
                  <div className="sec-integrity-badge" style={{ color: getIntegrityColor(stats.integrityStatus) }}>
                    <span className="sec-status-dot" style={{ background: getIntegrityColor(stats.integrityStatus) }} />
                    {stats.integrityStatus === 'HEALTHY' ? '✅ HEALTHY' : '🚨 CHAIN TAMPERED'}
                  </div>
                  <div className="sec-detail">
                    <span>Last check:</span>
                    <span>{formatTs(stats.lastIntegrityCheck)}</span>
                  </div>
                  {stats.integrityStatus === 'CHAIN_TAMPERED' && (
                    <div className="sec-warning">⚠️ Chuỗi blockchain có thể bị giả mạo. Hãy kiểm tra ngay!</div>
                  )}
                  <div className="sec-security-features">
                    <div className="sec-feature-row">
                      <span className="sec-feature-name">⛏️ Proof of Work</span>
                      <span className="sec-feature-badge on">✓ ENABLED</span>
                    </div>
                    <div className="sec-feature-row">
                      <span className="sec-feature-name">🔐 HMAC Node Auth</span>
                      <span className="sec-feature-badge on">✓ ENABLED</span>
                    </div>
                    <div className="sec-feature-row">
                      <span className="sec-feature-name">✍️ Digital Signature</span>
                      <span className="sec-feature-badge on">✓ ENABLED</span>
                    </div>
                    <div className="sec-feature-row">
                      <span className="sec-feature-name">🛡️ Consensus (2/3)</span>
                      <span className="sec-feature-badge on">✓ ENABLED</span>
                    </div>
                  </div>
                </div>

                {/* Node list card */}
                <div className="sec-card">
                  <h2>🖥️ Node Status <span className="sec-node-hint">— click để xem blocks</span></h2>
                  <div className="sec-node-list">
                    {(stats.nodes || []).map(node => {
                      const c = getNodeColor(node.status);
                      return (
                        <div
                          key={node.nodeId}
                          className={`sec-node-item sec-node-clickable${node.status === 'TAMPERED' ? ' tampered-highlight' : ''}`}
                          style={{ background: c.bg, borderColor: c.color }}
                          onClick={() => openNodeModal(node)}
                        >
                          <div className="sec-node-left">
                            <span className="sec-status-dot" style={{ background: c.dot }} />
                            <span className="sec-node-id">{node.nodeId}</span>
                          </div>
                          <div className="sec-node-right">
                            <span className="sec-node-status" style={{ color: c.color }}>{node.status}</span>
                            {node.lastKnownBlockIndex != null && (
                              <span className="sec-node-blocks">Block #{node.lastKnownBlockIndex}</span>
                            )}
                            {node.status !== 'OFFLINE' && (
                              <span className="sec-node-view-hint">🔍 Xem blocks</span>
                            )}
                            {node.status === 'TAMPERED' && (
                              <div className="sec-node-actions" onClick={e => e.stopPropagation()}>
                                <select
                                  value={selectedSource[node.nodeId] || ''}
                                  onChange={e => setSelectedSource(prev => ({ ...prev, [node.nodeId]: e.target.value }))}
                                  className="sec-node-select"
                                >
                                  <option value="">Chọn node nguồn...</option>
                                  {stats.nodes
                                    .filter(n => n.status === 'ONLINE' && n.nodeId !== node.nodeId)
                                    .map(n => <option key={n.nodeId} value={n.nodeId}>{n.nodeId}</option>)
                                  }
                                </select>
                                <button
                                  onClick={() => recoverNode(node.nodeId, selectedSource[node.nodeId])}
                                  disabled={!selectedSource[node.nodeId] || recoveringNodes[node.nodeId]}
                                  className="sec-node-recover-btn"
                                >
                                  {recoveringNodes[node.nodeId]
                                    ? '⏳ Đang phục hồi...'
                                    : `🔄 Recover from ${selectedSource[node.nodeId] || '?'}`}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {(!stats.nodes || stats.nodes.length === 0) && (
                      <div className="sec-no-nodes">Không có node nào được cấu hình</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tab: Audit */}
          {activeTab === 'audit' && (
            <div className="sec-tab-content">
              <div className="sec-card sec-audit-card">
                <h2>📋 Audit Log từ Blockchain</h2>
                {auditLoading ? (
                  <div className="sec-loading">Đang tải audit log...</div>
                ) : auditBlocks.length === 0 ? (
                  <div className="sec-no-data">Chưa có audit records nào trên blockchain</div>
                ) : (
                  <>
                    <div className="sec-audit-scroll">
                      <table className="sec-table">
                        <thead>
                          <tr>
                            <th>Block #</th>
                            <th>Thời gian</th>
                            <th>Admin ID</th>
                            <th>Hành động</th>
                            <th>Target ID</th>
                          </tr>
                        </thead>
                        <tbody>
                          {auditBlocks.map(block => (
                            <tr key={block.blockIndex}>
                              <td>{block.blockIndex}</td>
                              <td>{formatTs(block.timestamp)}</td>
                              <td>{block.adminId || '-'}</td>
                              <td><span className="sec-audit-action">{block.action || 'AUDIT'}</span></td>
                              <td>{block.targetId || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {auditTotalPages > 1 && (
                      <div className="sec-pagination">
                        <button onClick={() => fetchAuditBlocks(auditPage - 1)} disabled={auditPage === 0} className="sec-page-btn">◀ Trước</button>
                        <span>Trang {auditPage + 1} / {auditTotalPages}</span>
                        <button onClick={() => fetchAuditBlocks(auditPage + 1)} disabled={auditPage >= auditTotalPages - 1} className="sec-page-btn">Sau ▶</button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
          {/* Tab: Attack Simulation */}
          {activeTab === 'attack' && (
            <div className="sec-tab-content">
              <div className="sec-attack-grid">

                {/* Card 1: Tamper Data */}
                <div className="sec-attack-card">
                  <div className="sec-attack-icon">🔧Tamper Block Data</div>
                  <div className="sec-attack-name"></div>
                  <div className="sec-attack-desc">
                    Sửa <code>dataHash</code> của block cuối trên node mục tiêu.
                    Chain linkage vẫn OK nhưng hash không khớp dữ liệu thực.
                    <br/><strong>Kết quả:</strong> Node báo <span className="sec-status-tampered">TAMPERED</span>
                  </div>
                  <select className="sec-attack-select"
                    value={attackTargets.tamperData}
                    onChange={e => setAttackTargets(p => ({ ...p, tamperData: e.target.value }))}>
                    {ATTACK_NODES.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                  <button className="sec-attack-btn danger"
                    disabled={attackLoading.tamperData}
                    onClick={() => runAttack('tamper-data', attackTargets.tamperData)}>
                    {attackLoading.tamperData ? '⏳ Đang thực hiện...' : '💥 Tamper Block'}
                  </button>
                </div>

                {/* Card 2: Corrupt Chain */}
                <div className="sec-attack-card">
                  <div className="sec-attack-icon">⛓️ Corrupt Chain</div>
                  <div className="sec-attack-name"></div>
                  <div className="sec-attack-desc">
                    Sửa <code>currentHash</code> của block giữa chain, làm đứt hash linkage.
                    Block tiếp theo có <code>previousHash</code> không khớp.
                    <br/><strong>Kết quả:</strong> Node báo <span className="sec-status-tampered">TAMPERED</span> ngay lập tức
                  </div>
                  <select className="sec-attack-select"
                    value={attackTargets.corruptChain}
                    onChange={e => setAttackTargets(p => ({ ...p, corruptChain: e.target.value }))}>
                    {ATTACK_NODES.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                  <button className="sec-attack-btn danger"
                    disabled={attackLoading.corruptChain}
                    onClick={() => runAttack('corrupt-chain', attackTargets.corruptChain)}>
                    {attackLoading.corruptChain ? '⏳ Đang thực hiện...' : '💥 Corrupt Chain'}
                  </button>
                </div>

                {/* Card 3: Kill Node */}
                <div className="sec-attack-card">
                  <div className="sec-attack-icon">💀 Kill Node</div> 
                  <div className="sec-attack-desc">
                    Đánh dấu node là <code>OFFLINE</code> trong registry.
                    Consensus sẽ bỏ qua node này.
                    <br/><strong>Kết quả:</strong> Dashboard hiện <span className="sec-status-offline">OFFLINE</span>, consensus chạy với 2/3 nodes
                  </div>
                  <select className="sec-attack-select"
                    value={attackTargets.killNode}
                    onChange={e => setAttackTargets(p => ({ ...p, killNode: e.target.value }))}>
                    {ATTACK_NODES.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                  <button className="sec-attack-btn warning"
                    disabled={attackLoading.killNode}
                    onClick={() => runAttack('kill-node', attackTargets.killNode)}>
                    {attackLoading.killNode ? '⏳ Đang thực hiện...' : '⚡ Kill Node'}
                  </button>
                </div>

                {/* Card 4: Restore Node */}
                <div className="sec-attack-card"> 
                  <div className="sec-attack-icon">🔄 Restore Node</div>
                  <div className="sec-attack-desc">
                    Đưa node bị simulate-kill về trạng thái <code>ONLINE</code>.
                    Health check tự động sẽ xác nhận trạng thái thực.
                    <br/><strong>Kết quả:</strong> Dashboard hiện <span className="sec-status-online">ONLINE</span>
                  </div>
                  <select className="sec-attack-select"
                    value={attackTargets.restoreNode}
                    onChange={e => setAttackTargets(p => ({ ...p, restoreNode: e.target.value }))}>
                    {ATTACK_NODES.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                  <button className="sec-attack-btn success"
                    disabled={attackLoading.restoreNode}
                    onClick={() => runAttack('restore-node', attackTargets.restoreNode)}>
                    {attackLoading.restoreNode ? '⏳ Đang thực hiện...' : '✅ Restore Node'}
                  </button>
                </div>

              </div>

              {/* Attack Log */}
              {attackLogs.length > 0 && (
                <div className="sec-card sec-attack-log-card">
                  <div className="sec-attack-log-header">
                    <h2>📜 Attack Log</h2>
                    <button className="sec-attack-clear" onClick={() => setAttackLogs([])}>Xóa log</button>
                  </div>
                  <div className="sec-audit-scroll">
                    <table className="sec-table">
                      <thead>
                        <tr><th>Thời gian</th><th>Attack</th><th>Target</th><th>Kết quả</th><th>Mô tả</th></tr>
                      </thead>
                      <tbody>
                        {attackLogs.map(log => (
                          <tr key={log.id}>
                            <td className="sec-attack-ts">{log.ts}</td>
                            <td><code>{log.type}</code></td>
                            <td>{log.targetNode}</td>
                            <td>
                              <span className={log.success ? 'sec-badge-danger' : 'sec-badge-warn'}>
                                {log.success ? '💥 SUCCESS' : 'FAILED'}
                              </span>
                            </td>
                            <td className="sec-attack-log-desc">{log.description}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
          {activeTab === 'backup' && (
            <div className="sec-tab-content">
              <div className="sec-card sec-audit-card">
                <div className="sec-backup-header">
                  <h2>💾 Blockchain Backup &amp; Restore</h2>
                  <button
                    className="sec-backup-now-btn"
                    onClick={createBackup}
                    disabled={backingUp}
                  >
                    {backingUp ? '⏳ Đang tạo...' : '💾 Backup Now'}
                  </button>
                </div>

                {backupsLoading ? (
                  <div className="sec-loading">Đang tải danh sách backup...</div>
                ) : backups.length === 0 ? (
                  <div className="sec-no-data">Chưa có backup nào. Nhấn "Backup Now" để tạo.</div>
                ) : (
                  <div className="sec-audit-scroll">
                    <table className="sec-table">
                      <thead>
                        <tr>
                          <th>Tên file</th>
                          <th>Dung lượng</th>
                          <th>Thời gian</th>
                          <th>Checksum</th>
                          <th>Nodes</th>
                          <th>Thao tác</th>
                        </tr>
                      </thead>
                      <tbody>
                        {backups.map(b => (
                          <tr key={b.filename}>
                            <td className="sec-backup-filename">{b.filename}</td>
                            <td>{formatSize(b.sizeBytes)}</td>
                            <td>{formatTs(b.createdAt)}</td>
                            <td>
                              <code className="sec-backup-checksum" title={b.checksum}>
                                {b.checksum && b.checksum !== 'N/A' ? b.checksum.slice(0, 8) + '…' : 'N/A'}
                              </code>
                            </td>
                            <td>{b.nodeCount} node{b.nodeCount !== 1 ? 's' : ''}</td>
                            <td>
                              <div className="sec-backup-actions">
                                <button
                                  className="sec-restore-btn"
                                  onClick={() => setConfirmRestore(b.filename)}
                                  disabled={restoringFile === b.filename || !!deletingFile}
                                >
                                  {restoringFile === b.filename ? '⏳' : '🔄 Restore'}
                                </button>
                                <button
                                  className="sec-delete-btn"
                                  onClick={() => deleteBackup(b.filename)}
                                  disabled={deletingFile === b.filename || !!restoringFile}
                                >
                                  {deletingFile === b.filename ? '⏳' : '🗑️'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Confirm Restore Dialog */}
      {confirmRestore && (
        <div className="sec-modal-overlay" onClick={() => setConfirmRestore(null)}>
          <div className="sec-confirm-dialog" onClick={e => e.stopPropagation()}>
            <div className="sec-confirm-icon">⚠️</div>
            <h3 className="sec-confirm-title">Xác nhận Restore</h3>
            <p className="sec-confirm-msg">
              Restore sẽ ghi đè chain hiện tại trên tất cả nodes bằng backup:
            </p>
            <code className="sec-confirm-filename">{confirmRestore}</code>
            <p className="sec-confirm-warn">Hành động này không thể hoàn tác!</p>
            <div className="sec-confirm-btns">
              <button className="sec-confirm-cancel" onClick={() => setConfirmRestore(null)}>
                Hủy
              </button>
              <button className="sec-confirm-ok" onClick={() => doRestore(confirmRestore)}>
                ✅ Xác nhận Restore
              </button>
            </div>
          </div>
        </div>
      )}
      {nodeModal && (
        <div className="sec-modal-overlay" onClick={closeNodeModal}>
          <div className="sec-modal" onClick={e => e.stopPropagation()}>
            <div className="sec-modal-header">
              <div className="sec-modal-title">
                <span className="sec-status-dot" style={{ background: getNodeColor(nodeModal.status).dot }} />
                <span>{nodeModal.nodeId}</span>
                <span className="sec-modal-subtitle">{nodeModal.url}</span>
              </div>
              <button className="sec-modal-close" onClick={closeNodeModal}>✕</button>
            </div>
            <div className="sec-modal-body">
              {nodeBlocksLoading ? (
                <div className="sec-loading">⏳ Đang tải blocks từ node...</div>
              ) : nodeBlocksError ? (
                <div className="sec-error">{nodeBlocksError}</div>
              ) : nodeBlocks.length === 0 ? (
                <div className="sec-no-data">Node này chưa có block nào</div>
              ) : (
                <>
                  <div className="sec-modal-summary">
                    Tổng cộng <strong>{nodeBlocks.length}</strong> blocks
                  </div>
                  <div className="sec-block-list">
                    {[...nodeBlocks].reverse().map(block => (
                      <div key={block.blockIndex} className="sec-block-card">
                        <div className="sec-block-top">
                          <span className="sec-block-index">#{block.blockIndex}</span>
                          <span className="sec-block-type" style={{ background: typeBadge(block.dataType) }}>{block.dataType}</span>
                          <span className="sec-block-time">{formatDt(block.timestamp)}</span>
                          {block.signature
                            ? <span className="sec-block-sig-badge verified" title="Chữ ký số hợp lệ">🔐 Signed</span>
                            : <span className="sec-block-sig-badge legacy" title="Block cũ không có chữ ký">📦 Legacy</span>
                          }
                        </div>
                        <div className="sec-block-hashes">
                          <div className="sec-block-hash-row">
                            <span className="sec-hash-label">Hash</span>
                            <span className="sec-hash-value">{shortHash(block.currentHash)}</span>
                          </div>
                          <div className="sec-block-hash-row">
                            <span className="sec-hash-label">Prev</span>
                            <span className="sec-hash-value">{shortHash(block.previousHash)}</span>
                          </div>
                          {block.signature && (
                            <div className="sec-block-hash-row">
                              <span className="sec-hash-label">Sig</span>
                              <span className="sec-hash-value sec-hash-sig">{block.signature.slice(0, 12)}…</span>
                            </div>
                          )}
                        </div>
                        {block.referenceId && block.referenceId !== '0' && (
                          <div className="sec-block-ref">Ref ID: {block.referenceId}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default SecurityDashboard;
